import "./ClientRegistrationAuthorizationToReleaseMedicalInformationComponent.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import _ from "lodash";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormikInputComponent from "../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormControlLabelComponent from "../../shared/components/form-control-label/FormControlLabelComponent";
import FormikPhoneInputComponent from "../../shared/components/formik-phone-input/FormikPhoneInputComponent";
import FormikDatePickerComponent
    from "../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import HorizontalLineComponent from "../../shared/components/horizontal-line/horizontal-line/HorizontalLineComponent";
import ButtonComponent from "../../shared/components/button/ButtonComponent";
import FormikTextAreaComponent from "../../shared/components/form-controls/formik-text-area/FormikTextAreaComponent";
import FormikCheckBoxComponent from "../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import ErrorComponent from "../../shared/components/error/ErrorComponent";
import {ImageConfig, Misc, Patterns} from "../../constants"
import * as Yup from "yup";
import {CommonService} from "../../shared/services";
import CheckBoxComponent from "../../shared/components/form-controls/check-box/CheckBoxComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../store/reducers";
import ESignApprovalComponent from "../../shared/components/e-sign-approval/ESignApprovalComponent";
import LinkComponent from "../../shared/components/link/LinkComponent";
import {getClientSignatureDetails, setClientAuthorizationToReleaseInformation} from "../../store/actions/client.action";
import SignaturePopupComponent from "../../shared/components/signature-popup/SignaturePopupComponent";
import ModalComponent from "../../shared/components/modal/ModalComponent";

interface ClientRegistrationAuthorizationToReleaseMedicalInformationComponentProps {
    onNext?: () => void,
    initialValues?: any,
    formId?: string,
    mode?: string,
    appointmentId: any,
    onSubmitComplete?: Function
}

const AuthorizationToReleaseMedicalInformationInitialValues: any = {
    patient_name: '',
    patient_ssn: '',
    patient_phone: '',
    patient_dob: '',
    patient_address: {
        address_line: '',
        zip_code: '',
        state: '',
        city: '',
    },
    authorized_records: [],
    comments: '',
    client_name: '',
    date: new Date(),
    signature: '',
    is_authorized_representative: false,
    is_terms_acknowledged: false,
    information_accessed_by: [
        {
            name: '',
            phone_number: '',
            address: {
                address_line: '',
                zip_code: '',
                state: '',
                city: ''
            }
        }
    ],
    is_authorization_to_RMI_form_filled: true
}

const authorizationToReleaseMedicalInformationValidationSchema = Yup.object({
    patient_name: Yup.string().required('Name is required'),
    patient_ssn: Yup.string().matches(/^\d{9}$/, 'SSN is not valid').nullable(),
    patient_phone: Yup.string().required('Phone is required'),
    patient_dob: Yup.string().required('Date is required'),
    date: Yup.string().nullable(),
    injury_details: Yup.string().required('Body Part/Injury required'),
    patient_address: Yup.object({
        address_line: Yup.string().required('Address is required'),
        state: Yup.string().required('State is required'),
        city: Yup.string().required('City is required'),
    }),
    information_accessed_by: Yup.array().of(
        Yup.object({
            name: Yup.string().required('Name is required'),
            phone_number: Yup.string().required('Phone number is required'),
        })
    ).min(1),
    is_acknowledged: Yup.boolean().oneOf([true], 'Please acknowledge to the terms and condition'),
    is_agreed_to_waiver_expiry: Yup.boolean().oneOf([true], 'Please acknowledge to the waiver and liability '),
    is_agreed_to_renew_waiver: Yup.boolean().oneOf([true], ''),
    is_authorized_representative: Yup.boolean(),
    representative_name: Yup.string().nullable().when("is_authorized_representative", {
        is: true,
        then: Yup.string().nullable().required('Authorized Representative Name is required')
    }),
    signature: Yup.string().required()
})
const ClientRegistrationAuthorizationToReleaseMedicalInformationComponent = (props: ClientRegistrationAuthorizationToReleaseMedicalInformationComponentProps) => {

    const {appointmentId, onSubmitComplete, onNext, mode, formId, initialValues} = props;
    const [authorizationToReleaseMedicalInformationFormValues, setAuthorizationToReleaseMedicalInformationFormValues] = useState<any>(_.cloneDeep(AuthorizationToReleaseMedicalInformationInitialValues));
    const [authorizedRecords, setAuthorizedRecords] = useState<any>([]);
    const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState<boolean>(false);
    const formikRef = useRef<any>(null);
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const {clientBasicDetails} = useSelector((state: IRootReducerState) => state.client);
    const {clientAuthorizationToReleaseInformation} = useSelector((state: IRootReducerState) => state.client);
    const dispatch = useDispatch();
    const {clientSignatureDetails} = useSelector((state: IRootReducerState) => state.client);


    const onSubmit = useCallback((values: any, {setSubmitting, setErrors, submitForm}: FormikHelpers<any>) => {

        const payload = _.cloneDeep(values);
        payload.appointment_id = appointmentId ? appointmentId : currentUser?.appointment_id;
        payload.is_onboard_rmi_form_filled = true;
        payload.injury_details = values?.injury_details || clientAuthorizationToReleaseInformation?.injury_details;
        payload.information_accessed_by = payload.information_accessed_by?.map((item: any) => {
            return {
                ...item,
                phone_number: item?.phone_number?.replace(/\D/g, "")
            }
        });
        if (!formId) {
            CommonService._client.ClientAuthorizationToReleaseInformation(payload)
                .then((response: any) => {
                    CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                    dispatch(setClientAuthorizationToReleaseInformation(response?.data));
                    onNext && onNext();
                }).catch((error: any) => {
                setSubmitting(false);
                CommonService.handleErrors(setErrors, error);
            });
        } else {
            CommonService._client.ClientAuthorizationToReleaseInformationUpdate(payload, formId)
                .then((response: any) => {
                    CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                    onSubmitComplete && onSubmitComplete(values);
                }).catch((error: any) => {
                setSubmitting(false);
                CommonService.handleErrors(setErrors, error);
            });
        }

    }, [onNext, formId, appointmentId, currentUser, clientAuthorizationToReleaseInformation?.injury_details, onSubmitComplete, dispatch]);


    useEffect(() => {
        dispatch(getClientSignatureDetails(true, appointmentId));
    }, [dispatch, appointmentId]);

    useEffect(() => {
        if (
            clientSignatureDetails &&
            currentUser &&
            clientBasicDetails
            // &&
            // authorizationToReleaseMedicalInformationFormValues?.signature !== clientSignatureDetails?.signature
        ) {
            setAuthorizationToReleaseMedicalInformationFormValues((prevState: any) => ({
                ...prevState,
                signature: clientSignatureDetails.signature,
                is_authorized_representative: clientSignatureDetails.is_authorized_representative,
                is_terms_acknowledged: clientSignatureDetails.is_terms_acknowledged,
                representative_name: clientSignatureDetails.representative_name,
                client_name: currentUser.first_name + ' ' + currentUser.last_name,
                date: new Date(),
                patient_name: clientBasicDetails.first_name + ' ' + clientBasicDetails.last_name,
                patient_ssn: clientBasicDetails.ssn,
                patient_phone: clientBasicDetails.primary_contact_info.phone,
                patient_dob: clientBasicDetails.dob,
                patient_address: {
                    address_line: clientBasicDetails.address.address_line,
                    zip_code: clientBasicDetails.address.zip_code,
                    state: clientBasicDetails.address.state,
                    city: clientBasicDetails.address.city,
                }
            }));
        }
    }, [clientSignatureDetails, currentUser, clientBasicDetails]);


    useEffect(() => {
        if (initialValues) {
            setAuthorizationToReleaseMedicalInformationFormValues(initialValues);
            setAuthorizedRecords(initialValues?.authorized_records);

        } else if (clientAuthorizationToReleaseInformation && clientAuthorizationToReleaseInformation !== {} && clientAuthorizationToReleaseInformation?.length !== 0 && mode !== 'add' && !formId) {
            setAuthorizationToReleaseMedicalInformationFormValues(clientAuthorizationToReleaseInformation);
            setAuthorizedRecords(clientAuthorizationToReleaseInformation?.authorized_records)
        } else if (clientBasicDetails) {
            const patientFormValues = {
                ...AuthorizationToReleaseMedicalInformationInitialValues,
                patient_name: clientBasicDetails?.first_name + ' ' + clientBasicDetails?.last_name,
                patient_ssn: clientBasicDetails?.ssn,
                patient_phone: clientBasicDetails?.primary_contact_info?.phone,
                patient_dob: clientBasicDetails?.dob,
                patient_address: {
                    address_line: clientBasicDetails?.address?.address_line,
                    zip_code: clientBasicDetails?.address?.zip_code,
                    state: clientBasicDetails?.address?.state,
                    city: clientBasicDetails?.address?.city,
                },
            }
            setAuthorizationToReleaseMedicalInformationFormValues(patientFormValues);
        }

    }, [currentUser, clientBasicDetails, initialValues, clientAuthorizationToReleaseInformation, formId, mode]);

    const handleSignaturePopupClose = useCallback(() => {
        setIsSignaturePopupOpen(false);
    }, []);


    return (
        <div
            className={'client-registration-authorization-to-release-medical-information-component client-registration-details-block'}>
            <div className="client-registration-details-block__header">
                <div className="client-registration-details-block__title">
                    Authorization to Release Medical Information
                </div>
            </div>
            <Formik initialValues={authorizationToReleaseMedicalInformationFormValues}
                    validateOnChange={false}
                    validationSchema={authorizationToReleaseMedicalInformationValidationSchema}
                    validateOnBlur={true}
                    innerRef={formikRef}
                    enableReinitialize={true}
                    validateOnMount={true}
                    onSubmit={onSubmit}>
                {({
                      values,
                      touched,
                      errors,
                      setFieldValue,
                      validateForm,
                      isValid,
                      isSubmitting,
                      dirty
                  }) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);
                    return (
                        <Form className={'t-form'} noValidate={true}>
                            {/*<FormDebuggerComponent values={values} errors={errors} showDebugger={true}/>*/}
                            <div className={'t-form-controls'}>
                                <div className={'authorization-medical-information-text'}>
                                    I understand that this authorization is voluntary, and the information is
                                    for use by the
                                    recipient named below only,
                                    and may not be disclosed to any other individual or agency without my
                                    expressed consent
                                    or
                                    as otherwise
                                    provided by law.
                                </div>
                                <FormControlLabelComponent className={'patient-information-heading'}
                                                           label={'Part 1: Client Information'}/>
                                {/*<FormDebuggerComponent values={values} errors={errors}/>*/}
                                <div className={'ts-row'}>
                                    <div className={'ts-col-lg-6'}>
                                        <Field name={'patient_name'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={"full_name_input"}
                                                        label={'Name of Client'}
                                                        placeholder={'Enter Full Name'}
                                                        required={true}
                                                        titleCase={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className={'ts-col-lg-6'}>
                                        <Field name={'patient_ssn'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={"social_security_input"}
                                                        label={'Social Security Number'}
                                                        required={false}
                                                        type={'text'}
                                                        validationPattern={Patterns.NINE_DIGITS_ONLY}
                                                        titleCase={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className={'ts-row'}>
                                    <div className={'ts-col-lg-6'}>
                                        <Field name={'patient_phone'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikPhoneInputComponent
                                                        id={"phone_number_input"}
                                                        label={'Phone Number'}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className={'ts-col-lg-6'}>
                                        <Field name={'patient_dob'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        id={"dob_input"}
                                                        label={'Date of Birth'}
                                                        placeholder={'Enter your date of birth'}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className={'ts-row'}>
                                    <div className={'ts-col-lg-6'}>
                                        <Field name={'patient_address.address_line'}
                                               className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={"address_input"}
                                                        label={'Address'}
                                                        placeholder={'Enter your Address'}
                                                        formikField={field}
                                                        required={true}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className={'ts-col-lg-6'}>
                                        <Field name={'patient_address.zip_code'}
                                               className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={"zip_input"}
                                                        label={'ZIP Code'}
                                                        validationPattern={Patterns.FIVE_DIGITS_ONLY}
                                                        placeholder={'Enter your ZIP Code'}
                                                        formikField={field}
                                                        required={true}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className={'ts-row'}>
                                    <div className={'ts-col-lg-6'}>
                                        <Field name={'patient_address.city'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={"city_input"}
                                                        label={'City'}
                                                        placeholder={'Enter your City'}
                                                        formikField={field}
                                                        required={true}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className={'ts-col-lg-6'}>
                                        <Field name={'patient_address.state'}
                                               className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={"state_input"}
                                                        label={'State'}
                                                        placeholder={'Enter your State'}
                                                        formikField={field}
                                                        required={true}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>

                                </div>
                                <HorizontalLineComponent/>
                                <FormControlLabelComponent className={'patient-information-heading'}
                                                           label={'Part 2: Client Authorization'}/>
                                <div className={'authorization-medical-information-text'}>
                                    <div>I, the undersigned patient, hereby give authorization to the
                                        following
                                        entity and its staff,
                                    </div>
                                    <div className={'heading'}><i>KINERGY SPORTS MEDICINE AND PERFORMANCE</i></div>
                                    <div>to communicate directly and release, to the person/entity listed
                                        below,
                                        the
                                        following information in regard to
                                        my:
                                    </div>
                                </div>
                                <div className={'ts-row'}>
                                    <div className={'ts-col-lg-12'}>
                                        <Field name={'injury_details'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikTextAreaComponent
                                                        id={"body_part_input"}
                                                        label={'Insert Body Part/Injury'}
                                                        placeholder={'Enter Body Part/Injury'}
                                                        formikField={field}
                                                        required={true}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className={'ts-row'}>
                                    {CommonService._staticData?.AuthorizedRecords.map((record: string, index: number) => (

                                        <div className={'ts-col-6'} key={index + record}>
                                            {record !== 'other' && <CheckBoxComponent
                                                label={CommonService.capitalizeFirstLetter(record)}
                                                onChange={(isChecked: boolean) => {
                                                    const tempRecords = [...authorizedRecords];
                                                    if (isChecked) {
                                                        tempRecords.push(record);
                                                    } else {
                                                        _.remove(tempRecords, (item) => item === record);
                                                    }
                                                    setAuthorizedRecords(tempRecords)
                                                    setFieldValue('authorized_records', tempRecords);
                                                }}
                                                checked={authorizedRecords?.includes(record)}
                                            />}
                                            {record === 'other' && <CheckBoxComponent
                                                label={CommonService.capitalizeFirstLetter(record) + ' (Please specify below)'}
                                                onChange={(isChecked: boolean) => {
                                                    const tempRecords = [...authorizedRecords];
                                                    if (isChecked) {
                                                        tempRecords.push(record);
                                                    } else {
                                                        _.remove(tempRecords, (item) => item === record);
                                                    }
                                                    setAuthorizedRecords(tempRecords)
                                                    setFieldValue('authorized_records', tempRecords);
                                                }}
                                                checked={authorizedRecords?.includes(record)}
                                            />}
                                        </div>
                                    ))}
                                </div>
                                {values?.authorized_records?.includes('other') &&
                                    <div className="ts-row" id={"comments-field"}>
                                        <div className="ts-col-12">
                                            <Field name={'comments'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikTextAreaComponent
                                                            placeholder={"Enter your comments here"}
                                                            required={values?.option}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            autoFocus={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>}
                                <div className={'disclose-info'}>
                                    You may disclose this information to:
                                </div>
                                <FieldArray name={'information_accessed_by'}
                                            render={(arrayHelpers) => (
                                                <>
                                                    {values.information_accessed_by && values?.information_accessed_by?.map((item: any, index: any) => {
                                                        return (
                                                            <div key={index}>
                                                                {values?.information_accessed_by?.length > 1 && <div
                                                                    className={'contact-title-and-remove-button-wrapper'}>
                                                                    <div>Contact {index + 1}</div>
                                                                    <ButtonComponent
                                                                        className={'mrg-left-10'}
                                                                        color={'error'}
                                                                        variant={'outlined'}
                                                                        size={'small'}
                                                                        prefixIcon={
                                                                            <ImageConfig.DeleteIcon/>}
                                                                        onClick={() => {
                                                                            arrayHelpers.remove(index)
                                                                        }}
                                                                    >
                                                                        Remove
                                                                    </ButtonComponent>
                                                                </div>
                                                                }
                                                                <div className={'ts-row'}>
                                                                    <div className={'ts-col-lg-6'}>
                                                                        <Field
                                                                            name={`information_accessed_by[${index}].name`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikInputComponent
                                                                                        formikField={field}
                                                                                        label={'Full Name'}
                                                                                        titleCase={true}
                                                                                        fullWidth={true}
                                                                                        required={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                    <div className={'ts-col-lg-6'}>
                                                                        <Field
                                                                            name={`information_accessed_by[${index}].phone_number`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikPhoneInputComponent
                                                                                        formikField={field}
                                                                                        label={'Phone Number'}
                                                                                        fullWidth={true}
                                                                                        required={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                </div>
                                                                <div className={'ts-row'}>
                                                                    <div className={'ts-col-lg-6'}>
                                                                        <Field
                                                                            name={`information_accessed_by[${index}].address.address_line`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikInputComponent
                                                                                        formikField={field}
                                                                                        label={'Address'}
                                                                                        titleCase={true}
                                                                                        fullWidth={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>

                                                                    <div className={'ts-col-lg-6'}>
                                                                        <Field
                                                                            name={`information_accessed_by[${index}].address.zip_code`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikInputComponent
                                                                                        formikField={field}
                                                                                        validationPattern={Patterns.FIVE_DIGITS_ONLY}
                                                                                        label={'ZIP Code'}
                                                                                        titleCase={true}
                                                                                        fullWidth={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                </div>
                                                                <div className={'ts-row'}>
                                                                    <div className={'ts-col-lg-6'}>
                                                                        <Field
                                                                            name={`information_accessed_by[${index}].address.city`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikInputComponent
                                                                                        formikField={field}
                                                                                        label={'City'}
                                                                                        titleCase={true}
                                                                                        fullWidth={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                    <div className={'ts-col-lg-6'}>
                                                                        <Field
                                                                            name={`information_accessed_by[${index}].address.state`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikInputComponent
                                                                                        formikField={field}
                                                                                        label={'State'}
                                                                                        titleCase={true}
                                                                                        fullWidth={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                </div>
                                                                <div className={'ts-row'}>
                                                                    {
                                                                        values?.information_accessed_by?.length > 1 &&
                                                                        <HorizontalLineComponent/>
                                                                    }
                                                                    {
                                                                        (index === values?.information_accessed_by?.length - 1) && values.information_accessed_by.length < 5 &&
                                                                        <ButtonComponent
                                                                            className={'mrg-bottom-30'}
                                                                            prefixIcon={
                                                                                <ImageConfig.AddIcon/>}
                                                                            onClick={() => {
                                                                                arrayHelpers.push({
                                                                                    name: '',
                                                                                    phone_number: '',
                                                                                    address: {
                                                                                        address_line: '',
                                                                                        zip: '',
                                                                                        state: '',
                                                                                        city: ''
                                                                                    }
                                                                                })
                                                                            }}
                                                                        >
                                                                            Add Another Contact
                                                                        </ButtonComponent>
                                                                    }
                                                                </div>

                                                            </div>
                                                        )
                                                    })}
                                                </>
                                            )}/>
                                <div className={'purpose-of-disclosure'}>
                                    Purpose of Disclosure:
                                </div>
                                <div className={'purpose-of-disclosure-text'}>
                                    I understand that the purpose of this authorization is to provide
                                    relevant
                                    pieces of my protected health information to the person/entity
                                    listed
                                    above,
                                    to facilitate communication, coordination, and
                                    continuity of care, and promote efficiency and effectiveness of
                                    treatment.
                                </div>
                                <div className={'expiration-date-of-authorization'}>
                                    Expiration Date of Authorization:
                                </div>
                                <div className={'expiration-date-of-authorization-text'}>
                                    This authorization is valid for twelve (12) months from the date of
                                    signing.
                                </div>
                                <HorizontalLineComponent/>
                                <div className={'patient-right-heading'}>
                                    Part 3: Client Rights
                                </div>
                                <div className={'patient-right-text'}>
                                    <ul>
                                        <li className={'pdd-bottom-10'}>I may revoke this authorization at any time
                                            prior to its expiration by
                                            providing written notice of revocation to the entity providing the
                                            information. I understand I
                                            cannot revoke this authorization retroactively for information already
                                            released.
                                        </li>

                                        <li> I may see and copy the information described on this form if I
                                            ask
                                            for
                                            it.
                                        </li>
                                    </ul>
                                </div>

                                <div className="ts-row pdd-top-30">
                                    <div className="ts-col-12">
                                        <Field name={'is_terms_acknowledged'}
                                               className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikCheckBoxComponent
                                                        label={'I hereby authorize Kinergy Sports Medicine and Performance and its staff to use or disclose my protected\n' +
                                                            'health information as described in Part 2.'}
                                                        formikField={field}
                                                        color={'accent-color'}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                <div className="ts-row">
                                    <div className="ts-col-12">
                                        <Field name={'is_authorized_representative'}
                                               className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikCheckBoxComponent
                                                        label={'I am an Authorized Representative for the client (ie. Parent, Legal Guardian, Agent, Manager, etc.)'}
                                                        formikField={field}
                                                        color={'accent-color'}
                                                        onChange={() => {
                                                            setFieldValue('representative_name', '');
                                                        }}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                {
                                    values.is_authorized_representative && <div className="ts-row">
                                        <div className="ts-col-6">
                                            <Field name={'representative_name'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Authorized Representative Name'}
                                                            placeholder={'Enter Representative Name'}
                                                            required={true}
                                                            fullWidth={true}
                                                            formikField={field}
                                                            titleCase={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                }
                                <div className="ts-row mrg-top-20">
                                    <div className="ts-col-md-12 ts-col-lg-6">
                                        <Field name={'client_name'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={"name_input"}
                                                        label={"Client's Name"}
                                                        placeholder={"Enter Client's Name"}
                                                        required={true}
                                                        disabled={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        titleCase={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="ts-col-md-12 ts-col-lg-6">
                                        <Field name={'date'} className="t-form-control">
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        id={"dob_input"}
                                                        label={'Date'}
                                                        placeholder={'Enter Date'}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        disabled={true}
                                                        readOnly={true}
                                                        // clearable={false}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                </div>
                                {!(values?.signature && authorizationToReleaseMedicalInformationFormValues?.signature) &&
                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <FormControlLabelComponent
                                                className={"font-weight-thin"}
                                                label={"Signature of Client or Authorized Representative"}
                                                required={true}/>
                                            <ButtonComponent
                                                className={'add-signature-button'}
                                                onClick={() => {
                                                    setIsSignaturePopupOpen(true);
                                                }}
                                                prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                variant={'link'}
                                                color={'info'}
                                                size={'medium'}
                                            >
                                                Add Signature
                                            </ButtonComponent>
                                        </div>
                                        {
                                            (_.get(touched, "signature") && !!(_.get(errors, "signature"))) &&
                                            <ErrorComponent
                                                errorText={(_.get(errors, "signature"))}/>
                                        }
                                    </div>
                                }
                                {(values?.signature) &&
                                    <>
                                        <ESignApprovalComponent isSigned={true}
                                                                signature_url={values?.signature}
                                        />
                                        <div className={'signature-remove-button'}>
                                            <LinkComponent
                                                onClick={() => {
                                                    setFieldValue('signature', ''); // Clear only the signature field
                                                }}
                                            >
                                                Remove Signature
                                            </LinkComponent>
                                        </div>
                                    </>
                                }

                                <div className="t-form-actions">
                                    <ButtonComponent
                                        suffixIcon={<ImageConfig.ArrowRightCircle/>}
                                        id={"submit_btn"}
                                        type={"submit"}
                                        size={'large'}
                                        className={'mrg-bottom-30'}
                                        // onClick={()=>{
                                        //     commonService.scrollToErrorField(formikRef)
                                        // }}
                                        disabled={!isValid || isSubmitting}
                                        isLoading={isSubmitting}
                                    >
                                        {!formId && mode !== 'add' ? "Next" : "Submit"}
                                    </ButtonComponent>
                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
            <ModalComponent isOpen={isSignaturePopupOpen}
                            onClose={handleSignaturePopupClose}
                            size={"xl"}
            >
                <SignaturePopupComponent
                    onSign={(signImage: any) => {
                        formikRef?.current.setFieldValue('signature', signImage)
                        setAuthorizationToReleaseMedicalInformationFormValues({
                            ...formikRef?.current?.values,
                            signature: signImage
                        })
                        handleSignaturePopupClose();
                    }}
                    handleSignaturePopupClose={handleSignaturePopupClose}
                />
            </ModalComponent>
        </div>
    );

};

export default ClientRegistrationAuthorizationToReleaseMedicalInformationComponent;
